var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const CACHE_PREFIXES = ['orbscape-cache'];
const SESSION_FLAG = 'orbscape:pendingCleanReload';
const LAST_REASON = 'orbscape:lastReloadReason';
const BOOT_HISTORY = 'orbscape:bootHistory';
const SAFE_MODE_FLAG = 'orbscape:safeMode';
const CRASH_LOOP_WINDOW_MS = 2 * 60 * 1000;
const CRASH_LOOP_THRESHOLD = 3;
const MAX_BOOT_ENTRIES = 6;
function inBrowser() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}
function purgeCaches() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!inBrowser() || typeof caches === 'undefined')
            return;
        try {
            const keys = yield caches.keys();
            const deletions = keys
                .filter(key => CACHE_PREFIXES.some(prefix => key.startsWith(prefix)))
                .map(key => caches.delete(key).catch(() => false));
            yield Promise.all(deletions);
        }
        catch (err) {
            console.warn('[Recovery] cache purge failed', err);
        }
    });
}
function unregisterServiceWorkers() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!inBrowser() || !('serviceWorker' in navigator))
            return;
        try {
            const regs = yield navigator.serviceWorker.getRegistrations();
            yield Promise.all(regs.map(reg => reg.unregister().catch(() => false)));
        }
        catch (err) {
            console.warn('[Recovery] service worker unregister failed', err);
        }
    });
}
export function markReloadPending(reason) {
    if (!inBrowser())
        return;
    try {
        sessionStorage.setItem(SESSION_FLAG, '1');
        sessionStorage.setItem(LAST_REASON, reason);
    }
    catch (err) {
        console.warn('[Recovery] failed to mark reload pending', err);
    }
}
export function clearReloadFlag() {
    if (!inBrowser())
        return;
    try {
        sessionStorage.removeItem(SESSION_FLAG);
        sessionStorage.removeItem(LAST_REASON);
    }
    catch (err) {
        console.warn('[Recovery] failed to clear reload flag', err);
    }
}
export function forceCleanReload(reason = 'unknown') {
    return __awaiter(this, void 0, void 0, function* () {
        if (!inBrowser())
            return;
        console.warn(`[Recovery] Forcing clean reload (${reason})`);
        markReloadPending(reason);
        yield Promise.allSettled([
            purgeCaches(),
            unregisterServiceWorkers()
        ]);
        window.location.reload();
    });
}
export function applyPendingRecovery() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!inBrowser())
            return;
        const needsRecovery = sessionStorage.getItem(SESSION_FLAG) === '1';
        if (!needsRecovery)
            return;
        const reason = sessionStorage.getItem(LAST_REASON) || 'pending';
        console.warn(`[Recovery] Applying pending cleanup from previous session (${reason})`);
        yield Promise.allSettled([
            purgeCaches(),
            unregisterServiceWorkers()
        ]);
        clearReloadFlag();
    });
}
function readBootHistory() {
    if (!inBrowser())
        return [];
    try {
        const raw = localStorage.getItem(BOOT_HISTORY);
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return [];
        return parsed.filter((value) => typeof value === 'number');
    }
    catch (err) {
        console.warn('[Recovery] Failed to read boot history', err);
        return [];
    }
}
function writeBootHistory(entries) {
    if (!inBrowser())
        return;
    try {
        localStorage.setItem(BOOT_HISTORY, JSON.stringify(entries));
    }
    catch (err) {
        console.warn('[Recovery] Failed to write boot history', err);
    }
}
function recordBootAttempt(now) {
    if (!inBrowser()) {
        return { crashLoopDetected: false, bootCount: 0 };
    }
    const history = readBootHistory()
        .filter((timestamp) => now - timestamp < CRASH_LOOP_WINDOW_MS);
    history.push(now);
    const trimmed = history.slice(-MAX_BOOT_ENTRIES);
    writeBootHistory(trimmed);
    return {
        crashLoopDetected: trimmed.length >= CRASH_LOOP_THRESHOLD,
        bootCount: trimmed.length,
    };
}
export function isSafeModeEnabled() {
    if (!inBrowser())
        return false;
    return localStorage.getItem(SAFE_MODE_FLAG) === '1';
}
export function getSafeModeReason() {
    if (!inBrowser())
        return null;
    return localStorage.getItem(`${SAFE_MODE_FLAG}:reason`);
}
export function clearSafeMode() {
    if (!inBrowser())
        return;
    localStorage.removeItem(SAFE_MODE_FLAG);
}
function enableSafeMode(reason) {
    if (!inBrowser())
        return;
    try {
        localStorage.setItem(SAFE_MODE_FLAG, '1');
        localStorage.setItem(`${SAFE_MODE_FLAG}:reason`, reason);
        localStorage.setItem(`${SAFE_MODE_FLAG}:timestamp`, String(Date.now()));
    }
    catch (err) {
        console.warn('[Recovery] Failed to enable safe mode', err);
    }
}
export function applyCrashLoopRecovery() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!inBrowser()) {
            return { crashLoopDetected: false, bootCount: 0 };
        }
        const now = Date.now();
        const state = recordBootAttempt(now);
        if (!state.crashLoopDetected)
            return state;
        console.warn('[Recovery] Crash loop detected; entering cold boot safe mode', {
            bootCount: state.bootCount,
        });
        enableSafeMode('crash-loop');
        yield Promise.allSettled([
            purgeCaches(),
            unregisterServiceWorkers()
        ]);
        return state;
    });
}
